import { Fragment } from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useAppTheme } from '@/theme/ThemeContext'
import { describeNotificationChannel } from '@/services/notificationChannels'
import { describeNotificationStatus } from '@/services/notificationStatuses'
import type { SentMessage } from '@/services/notificationService'

// The time reads left to right inside the right-to-left card, as in the Figma.
const formatTime = (iso: string | null) =>
  iso ? dayjs(iso).format('HH:mm DD/MM/YYYY') : '—'

type Props = {
  message: SentMessage
  /** Name the alert type as a caption; the "כללי" tab needs it, the alert's own tab does not. */
  showAlertType?: boolean
}

const MessageCard = ({ message, showAlertType = false }: Props) => {
  const { mode } = useAppTheme()
  const dark = mode === 'dark'
  const colors = {
    text: dark ? '#fff' : '#003F61',
    secondary: dark ? '#93A3B5' : '#9AA4B2',
    muted: dark ? '#6B7A8C' : '#8F98A5',
  }
  const small = {
    color: colors.secondary,
    fontSize: '10px',
    fontFamily: 'Rubik, sans-serif',
    display: 'block',
  } as const
  const status = describeNotificationStatus(message.status)

  return (
    <Box component="article">
      {showAlertType && message.kind !== 'manual' && message.alertTypeName && (
        <Typography variant="caption" sx={{ ...small, color: colors.muted }}>
          {message.alertTypeName}
        </Typography>
      )}
      <Typography variant="caption" sx={small}>
        {`באמצעות: ${message.channels
          .map(channel => describeNotificationChannel(channel).label)
          .join(',')}`}
      </Typography>
      <Typography variant="caption" sx={{ ...small, mb: 0.5 }}>
        {message.addresses.map((address, index) => (
          <Fragment key={address}>
            {index > 0 && ', '}
            <bdi dir="ltr">{address}</bdi>
          </Fragment>
        ))}
      </Typography>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 800,
          color: colors.text,
          mb: 0.5,
          fontSize: '16px',
          fontFamily: 'Rubik, sans-serif',
        }}
      >
        {message.recipientName ?? '—'}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          whiteSpace: 'pre-line',
          color: colors.text,
          lineHeight: 1.2,
          mb: 0.75,
          fontSize: '13px',
          fontFamily: 'Rubik, sans-serif',
        }}
      >
        {message.bodyText}
      </Typography>
      {message.bodyIsTemplate && (
        <Typography
          variant="caption"
          sx={{ ...small, color: colors.muted, mb: 0.75 }}
        >
          תבנית ההודעה
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          columnGap: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{ ...small, color: colors.muted, fontSize: '11px' }}
        >
          נשלח: <bdi dir="ltr">{formatTime(message.sentAt)}</bdi>
        </Typography>
        <Typography
          variant="caption"
          sx={{ ...small, color: colors.muted, fontSize: '11px' }}
        >
          התקבל: <bdi dir="ltr">{formatTime(message.receivedAt)}</bdi>
        </Typography>
        <Tooltip
          title={message.lastError ?? ''}
          disableHoverListener={!message.lastError}
        >
          <Typography
            variant="caption"
            sx={{
              color: message.status === 'failed' ? 'error.main' : colors.muted,
              fontSize: '11px',
              fontFamily: 'Rubik, sans-serif',
            }}
          >
            {status.label}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  )
}

export default MessageCard
